import { useCallback, useEffect, useMemo, useState } from 'react';
import useGridFilters, { GridFilters } from 'src/careflow/ph-core/grid/hook/useGridFilters';

import {
  CareTeamParticipant,
  Coding,
  Reference,
} from 'src/careflow/ph-core/fhir-client/resources';
import { getCareTeamMemberGridRows, searchIfContainedInObj } from 'src/careflow/helpers/common';
import { CareTeamMemberGridRowData, CareTeamPermissions } from 'src/@types/crs/case';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import { debounce } from 'src/utils/timers';
import { isBetweenDates } from 'src/utils/dates';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import { fhirClient } from 'src/App';
import { getReferenceId, getResourcesAndRelatedResources } from 'src/utils/fhir';
import CareTeamMembersGrid, { CareTeamMemberFilters, initialCareTeamMemberFilters } from 'src/careflow/care-coordination/case/care-team-management/CareTeamMembersGrid/CareTeamMembersGrid';

interface CaseCareTeamMemberGridProps {
  patient: WrappedPatient;
  onMemberUpdateSuccess: Function;
  participants: CareTeamParticipant[];
  careTeam: WrappedCareTeam;
  permissions: CareTeamPermissions['members'] | null;
}

const CaseCareTeamMemberGrid = ({
  participants,
  patient,
  onMemberUpdateSuccess,
  careTeam,
  permissions,
}: CaseCareTeamMemberGridProps) => {
  const { filters, searchTextFieldValue, ...gridFiltersProps } =
    useGridFilters<CareTeamMemberFilters>(initialCareTeamMemberFilters);

  const [participantsWithResource, setParticipantsWithResource] = useState<any>([]);

  const [filteredCareTeamMembers, setFilteredCareTeamMembers] = useState<CareTeamMemberGridRowData[]>([]);

  const associatedOrgs = useMemo(
    () =>
      participants?.reduce<Reference[]>((associatedOrgsx, participant) => {
        if (!participant?.onBehalfOf?.reference) return associatedOrgsx;
        if (
          associatedOrgsx.find(({ reference }) => reference === participant?.onBehalfOf?.reference)
        )
          return associatedOrgsx;
        return [...associatedOrgsx, participant.onBehalfOf];
      }, []) ?? [],
    [participants]
  );

  const roles = useMemo(
    () =>
      participants?.reduce<Coding[]>(
        (allRoles, participant) => [
          ...allRoles,
          ...(participant?.role?.reduce<Coding[]>((roles, role) => {
            if (
              roles.find(({ code }) => code === role?.coding?.[0]?.code) ||
              allRoles.find(({ code }) => code === role?.coding?.[0]?.code)
            )
              return roles;
            if (!role.coding?.[0]) return roles;
            return [...roles, role.coding?.[0]];
          }, []) ?? []),
        ],
        []
      ),
    [participants]
  );

  const getParticipantsFhirResource = useCallback(async () => {
    if (!participants || participants.length === 0) return;
  
    const mapParticipantsFhirResource = await Promise.all(
      participants.map(async (participant) => {
        const reference = participant?.member?.reference;
        if (!reference) return null;
  
        const url = reference.includes('PractitionerRole')
          ? `PractitionerRole?_id=${getReferenceId(reference)}&_include=PractitionerRole:organization&_include=PractitionerRole:practitioner`
          : reference;
  
        try {
          const response = await fhirClient.search<any>(url);
  
          if (response?.resourceType === 'Bundle') {
            const resourceEntries =
              response?.entry?.reduce((resources: any[], { resource }: any) => {
                return [...resources, resource];
              }, []) ?? [];
  
            const [practitionerRole, relatedResources] = getResourcesAndRelatedResources(
              resourceEntries,
              'PractitionerRole'
            );
  
            return {
              resource: practitionerRole?.[0] ?? null,
              includedResources: relatedResources,
            };
          }
  
          return {
            resource: response,
            includedResources: [],
          };
        } catch (error) {
          return null;
        }
      })
    );
  
    const mapParticipants = mapParticipantsFhirResource.filter(Boolean);
    setParticipantsWithResource(mapParticipants);
  }, [participants]);

  useEffect(() => {
    getParticipantsFhirResource();
  }, [getParticipantsFhirResource]);

  const handleFiltersChange = useMemo(
    () =>
      debounce((careTeamMembers, filters, searchTextFieldValue) => {
        setFilteredCareTeamMembers(
          filterCareTeamMembers(careTeamMembers, { filters, searchTextFieldValue })
        );
      }, 800),
    []
  );

  useEffect(() => {
    const careTeamMembers = getCareTeamMemberGridRows(participants, participantsWithResource);
    handleFiltersChange(careTeamMembers, filters, searchTextFieldValue);
  }, [filters, searchTextFieldValue, handleFiltersChange, participants, participantsWithResource]);

  return (
    <PermissionsProvider permissions={permissions}>
      <CareTeamMembersGrid
        searchTextFieldValue={searchTextFieldValue}
        roles={roles}
        associatedOrgs={associatedOrgs}
        careTeam={careTeam}
        onMemberUpdateSuccess={onMemberUpdateSuccess}
        careTeamMembers={filteredCareTeamMembers}
        patient={patient}
        filterValues={filters}
        {...gridFiltersProps}
      />
    </PermissionsProvider>
  );
};

const filterCareTeamMembers = (
  careTeamMembers: CareTeamMemberGridRowData[],

  { filters, searchTextFieldValue }: GridFilters<CareTeamMemberFilters>
): CareTeamMemberGridRowData[] => {
  let filteredCareTeamMembers = [...careTeamMembers];

  const searchByString = searchTextFieldValue?.toLowerCase().trim() ?? '';
  if (searchByString.length >= 1) {
    filteredCareTeamMembers = filteredCareTeamMembers.filter((careTeam) =>
      searchIfContainedInObj(
        careTeam,
        [
          'name',
          'identifier',
          'roleName',
          'startDate',
          'endDate',
          'memberType',
          'associatedOrgName',
        ],
        searchByString
      )
    );
  }

  const { associatedOrg, endDate, memberTypes, role, startDate } = filters;

  return filteredCareTeamMembers.filter(
    ({ startDateObj, endDateObj, associatedOrgId, memberType, roleId }) => {
      if (startDate?.isValid?.() && endDate?.isValid?.()) {
        if (!startDateObj?.isValid() || !endDateObj?.isValid()) return false;

        if (!isBetweenDates(startDateObj, endDateObj, startDate, endDate)) return false;
      }

      if (startDate?.isValid?.()) {
        if (!startDateObj?.isValid()) return false;
        if (!startDateObj?.isSameOrAfter(startDate)) return false;
      }

      if (endDate?.isValid?.()) {
        if (!endDateObj?.isValid()) return false;
        if (!endDateObj.isSameOrBefore(endDate)) return false;
      }
      if (associatedOrg.length) {
        if (!associatedOrg.find(({ reference }) => reference === associatedOrgId)) return false;
      }
      if (memberTypes.length) {
        if (!memberTypes.find(({ value }) => value === memberType)) return false;
      }
      if (role.length) {
        if (!role.find(({ code }) => code === roleId)) return false;
      }
      return true;
    }
  );
};

export default CaseCareTeamMemberGrid;
